import type { AlarmExtend } from "../types";

// ─── Constants ────────────────────────────────────────────────────────────────
export const IMAGE_BASE_URL = `https://${import.meta.env.VITE_API_DOMAIN}`;

/**
 * Placeholder SVG data-URI used when an image fails to load or path is missing.
 * Self-contained — no network request, never 404.
 */
export const IMAGE_PLACEHOLDER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23f1f5f9'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='system-ui' font-size='14' fill='%2394a3b8'%3ENo Image%3C/text%3E%3C/svg%3E";

// ─── Image ────────────────────────────────────────────────────────────────────

/**
 * Build a full image URL from a relative path returned by the API.
 * Returns IMAGE_PLACEHOLDER on null / undefined / empty string.
 */
export const buildImageUrl = (path?: string | null): string => {
  if (!path || typeof path !== "string" || path.trim() === "") {
    return IMAGE_PLACEHOLDER;
  }
  // Avoid double-slash
  const base = IMAGE_BASE_URL.replace(/\/$/, "");
  const rel = path.startsWith("/") ? path : `/${path}`;
  return `${base}${rel}`;
};

/**
 * img onError handler — swaps broken src to placeholder.
 * Usage: <img onError={handleImageError} />
 */
export const handleImageError = (
  e: React.SyntheticEvent<HTMLImageElement>,
): void => {
  const target = e.currentTarget;
  if (target.src !== IMAGE_PLACEHOLDER) {
    target.src = IMAGE_PLACEHOLDER;
  }
};

// ─── Time ─────────────────────────────────────────────────────────────────────

/**
 * Format unix timestamp (seconds) to readable Indonesian locale string.
 * Returns "—" on invalid input.
 */
export const formatUnixTime = (unix?: number | null): string => {
  if (!unix || typeof unix !== "number" || isNaN(unix) || unix <= 0) return "—";
  try {
    return new Date(unix * 1000).toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "—";
  }
};

// ─── JSON parse ───────────────────────────────────────────────────────────────

/**
 * Safe JSON.parse wrapper.
 * Returns null instead of throwing on malformed input.
 */
export const safeParseJson = <T = unknown>(raw: unknown): T | null => {
  if (!raw || typeof raw !== "string") return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

/**
 * Parse the Extend JSON string from an AlarmItem.
 * Returns null on error — callers must handle null.
 */
export const parseAlarmExtend = (raw: unknown): AlarmExtend | null =>
  safeParseJson<AlarmExtend>(raw);

// ─── Attribute label extraction ───────────────────────────────────────────────

/**
 * Extract the label portion from "value:confidence" format.
 * e.g. "male:0.99" → "male", "hard_hat:0.99" → "hard hat"
 * Returns "—" on missing/invalid input.
 */
export const extractLabel = (val?: string | null): string => {
  if (!val || typeof val !== "string") return "—";
  const raw = val.split(":")[0] ?? "";
  return raw.replace(/_/g, " ").trim() || "—";
};

/**
 * Extract the confidence value from "value:confidence" format.
 * e.g. "male:0.99" → 0.99
 * Returns 0 on invalid input.
 */
export const extractConfidence = (val?: string | null): number => {
  if (!val || typeof val !== "string") return 0;
  const conf = parseFloat(val.split(":")[1] ?? "");
  return isNaN(conf) ? 0 : conf;
};

// ─── Confidence badge ──────────────────────────────────────────────────────────

/**
 * Tailwind class string for confidence badge color.
 * >= 80%  → green
 * >= 60%  → amber
 * < 60%   → red
 */
export const confidenceBadgeClass = (c: number): string => {
  if (c >= 0.8) return "text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200";
  if (c >= 0.6) return "text-amber-700 bg-amber-50 ring-1 ring-amber-200";
  return "text-red-700 bg-red-50 ring-1 ring-red-200";
};

// ─── Safe array ───────────────────────────────────────────────────────────────

/**
 * Ensures a value is always an array.
 * Returns [] on null / undefined / non-array.
 */
export const safeArray = <T>(val: unknown): T[] => {
  if (Array.isArray(val)) return val as T[];
  return [];
};
